import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import { orgTreeApi } from '@/services/org-tree'
import storeCommerceApi from '@/services/store-commerce'
import type { CountryTaxBundle, TaxLabel } from '@/types'

const formatPercent = (value: unknown) => {
  const numeric = Number(value ?? 0)
  return Number.isFinite(numeric) ? `${numeric.toFixed(2).replace(/\.00$/, '')}%` : '0%'
}

const normalizeCountryName = (value?: string | null) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (['united states', 'united states of america', 'us', 'usa'].includes(normalized)) return 'USA'
  if (['united arab emirates', 'uae', 'u.a.e'].includes(normalized)) return 'UAE'
  if (['uk', 'u.k', 'great britain', 'united kingdom'].includes(normalized)) return 'United Kingdom'
  if (['srilanka', 'sri lanka'].includes(normalized)) return 'Sri Lanka'
  if (normalized === 'canada') return 'Canada'
  if (normalized === 'india') return 'India'
  return value?.trim() || 'Other'
}

const getBundleCountryId = (bundle: CountryTaxBundle) => {
  const record = bundle as unknown as Record<string, unknown>
  const taxSystem = record.tax_system_data && typeof record.tax_system_data === 'object'
    ? record.tax_system_data as Record<string, unknown>
    : null
  return String(
    record.country_id
    ?? record.country
    ?? taxSystem?.country_id
    ?? taxSystem?.country
    ?? '',
  ).trim()
}

export default function CommerceTaxesPage() {
  const location = useLocation()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)

  const serviceInstanceQuery = useQuery({
    queryKey: ['commerce', 'tax-service-instance', serviceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
  })
  const serviceInstanceCountryId = serviceInstanceQuery.data?.country_id
  const serviceInstanceCountryName = normalizeCountryName(serviceInstanceQuery.data?.country_name)

  const countryBundlesQuery = useQuery({
    queryKey: ['commerce', 'country-tax-bundles', serviceInstanceCountryId ?? 'no-country'],
    queryFn: () => storeCommerceApi.listCountryTaxBundles({ active: true, country_id: serviceInstanceCountryId ?? undefined }),
    enabled: Boolean(serviceInstanceCountryId),
  })

  const taxLabelsQuery = useQuery({
    queryKey: ['commerce', 'tax-labels', serviceInstanceId],
    queryFn: () => storeCommerceApi.listTaxLabels({ service_instance_id: serviceInstanceId ?? undefined, active: 1 }),
  })

  const activatedBundleIds = useMemo(() => {
    return new Set(
      (taxLabelsQuery.data ?? [])
        .map((label) => String(label.country_tax_bundle ?? label.country_tax_bundle_data?.id ?? ''))
        .filter(Boolean),
    )
  }, [taxLabelsQuery.data])

  const activateMutation = useMutation({
    mutationFn: (bundle: CountryTaxBundle) =>
      storeCommerceApi.activateCountryTaxBundle({
        country_tax_bundle: bundle.id,
        name: bundle.name,
        description: bundle.description ?? bundle.name,
        service_instance_id: serviceInstanceId ?? undefined,
      }),
    onSuccess: () => {
      toast.success('Tax bundle activated')
      queryClient.invalidateQueries({ queryKey: ['commerce', 'tax-labels', serviceInstanceId] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const removeMutation = useMutation({
    mutationFn: (label: TaxLabel) =>
      storeCommerceApi.deactivateTaxLabel(label.id, { service_instance_id: serviceInstanceId ?? undefined }),
    onSuccess: () => {
      toast.success('Tax bundle removed')
      queryClient.invalidateQueries({ queryKey: ['commerce', 'tax-labels', serviceInstanceId] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const countryBundles = countryBundlesQuery.data ?? []
  const taxLabels = taxLabelsQuery.data ?? []
  const resolvedCountryName = serviceInstanceCountryName !== 'Other'
    ? serviceInstanceCountryName
    : normalizeCountryName(countryBundles[0]?.country_name)
  const relevantCountryBundles = countryBundles.filter((bundle) => {
    const bundleCountryId = getBundleCountryId(bundle)
    if (bundleCountryId && serviceInstanceCountryId) {
      return bundleCountryId === String(serviceInstanceCountryId)
    }
    const bundleCountryName = normalizeCountryName(bundle.country_name)
    if (bundleCountryName !== 'Other') {
      return bundleCountryName === resolvedCountryName
    }
    return true
  })
  const bundlesByCountry = serviceInstanceCountryId
    ? [{ countryName: resolvedCountryName, bundles: relevantCountryBundles }]
    : []

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tax Setup</h1>
        <p className="text-sm text-muted-foreground">
          Activate tax bundles for this organisation/service instance country, then attach them to products, packages, jobs, and particulars.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Activated Tax Bundles</CardTitle>
          <CardDescription>Operational tax bundles available for products, packages, basket, and jobs.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {taxLabels.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tax bundles activated yet.</p>
          ) : (
            taxLabels.map((label: TaxLabel) => (
              <div key={String(label.id)} className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <div className="font-medium">{label.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {label.country_tax_bundle_data?.country_name ?? 'Custom'} · {formatPercent(label.total_percentage)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{label.active === 0 ? 'Inactive' : 'Active'}</Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={removeMutation.isPending}
                    onClick={() => removeMutation.mutate(label)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Country Tax Bundles</CardTitle>
          <CardDescription>
            Showing country-level statutory defaults for this organisation country
            {resolvedCountryName && resolvedCountryName !== 'Other' ? `: ${resolvedCountryName}` : ''}
            {serviceInstanceCountryId ? ` (country id: ${serviceInstanceCountryId})` : ''}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {serviceInstanceQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading organisation country...</p>
          ) : !serviceInstanceCountryId ? (
            <p className="text-sm text-muted-foreground">
              Set a country on this organisation/service instance before activating tax bundles.
            </p>
          ) : countryBundlesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading tax bundles...</p>
          ) : relevantCountryBundles.length === 0 ? (
            <p className="text-sm text-muted-foreground">No country tax bundles found for {resolvedCountryName}.</p>
          ) : (
            bundlesByCountry.map(({ countryName, bundles }) => (
              <section key={countryName} className="space-y-2 rounded-xl border border-border/70 p-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold">{countryName}</h2>
                  <Badge variant="outline">{bundles.length} bundle{bundles.length === 1 ? '' : 's'}</Badge>
                </div>
                {bundles.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No seeded taxes found for {countryName}.</p>
                ) : (
                  <div className="space-y-3">
                    {bundles.map((bundle) => {
                      const activated = activatedBundleIds.has(String(bundle.id))
                      return (
                        <div key={String(bundle.id)} className="flex items-start justify-between gap-4 rounded-lg border p-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{bundle.name}</span>
                              <Badge variant="outline">{formatPercent(bundle.total_percentage)}</Badge>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {bundle.country_name ?? 'Country'} · {bundle.tax_system_name ?? 'Tax system'}
                            </div>
                            <div className="flex flex-wrap gap-2 pt-1">
                              {(bundle.taxes ?? []).map((tax) => (
                                <Badge key={String(tax.id)} variant="secondary">
                                  {tax.name}: {formatPercent(tax.rate)}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            disabled={activated || activateMutation.isPending}
                            onClick={() => activateMutation.mutate(bundle)}
                          >
                            {activated ? 'Activated' : 'Activate'}
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </section>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  )
}
